import React, { useContext, useRef } from 'react';
import { BasketContext } from 'layouts/aaro/fastPrice';
import Icon from '@mui/material/Icon';
import { useQueryClient } from '@tanstack/react-query';

import {
    DataGridPro,
    useGridApiRef,
    Toolbar,
    ToolbarButton,
    ColumnsPanelTrigger,
    FilterPanelTrigger,
    ExportCsv,
    QuickFilter,
    QuickFilterControl,
    QuickFilterClear,
    QuickFilterTrigger,
} from '@mui/x-data-grid-pro';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import Badge from '@mui/material/Badge';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import pxToRem from 'assets/theme/functions/pxToRem.js';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import AaroButtons from '../aaroButtons';
import { formatPrice, calculateTaxPrice, calculateProfit, calculateDiscount } from 'helpers/priceHelpers';
import { getStockPrice } from 'services/outside/aaro/priceList';
import { todayIsBetween } from 'helpers/dateHelpers';
import { notify } from 'helpers/notificationHelper';
import { saveBasket, deleteBasket, copyBasket, updateBasket } from 'services/api/basket';
import MDButton from 'components/MDButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { config } from 'Constants';

// Styled components for the toolbar
const StyledQuickFilter = styled(QuickFilter)({
    display: 'grid',
    alignItems: 'center',
});

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity']),
}));

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    overflowX: 'clip',
    width: ownerState.expanded ? 260 : 'var(--trigger-width)',
    opacity: ownerState.expanded ? 1 : 0,
    transition: theme.transitions.create(['width', 'opacity']),
}));

// Modern profit chip component
const ProfitChip = styled(Chip)(({ theme, profitLevel }) => ({
    fontWeight: 500,
    fontSize: '0.7rem',
    height: '24px',
    borderRadius: '12px',
    minWidth: '60px',
    '& .MuiChip-label': {
        paddingLeft: '8px',
        paddingRight: '8px',
    },
    ...(profitLevel === 'high' && {
        backgroundColor: '#4caf50',
        color: 'white',
    }),
    ...(profitLevel === 'medium' && {
        backgroundColor: '#2196f3',
        color: 'white',
    }),
    ...(profitLevel === 'low' && {
        backgroundColor: '#ff9800',
        color: 'white',
    }),
    ...(profitLevel === 'critical' && {
        backgroundColor: '#f44336',
        color: 'white',
    }),
}));

export default function Basket(props) {
    const { basket: contextBasket, setBasket: setContextBasket } = useContext(BasketContext);

    // Props for handling different basket sources
    const {
        basketData = null, // External basket data (from DB)
        isReadOnly = false, // Read-only mode for saved baskets
        showSaveButton = true, // Show save button
        basketSource = 'context', // 'context' or 'external'
        title = null, // Custom title
        compactMode = false, // Compact mode for cards
        onBasketSave = null, // Callback when basket is saved to DB
    } = props;

    // Use external basket data or context basket
    const [localBasket, setLocalBasket] = React.useState([]);

    // Initialize local basket when basketData changes
    React.useEffect(() => {
        if (basketSource === 'external' && basketData?.items) {
            setLocalBasket(basketData.items);
        }
    }, [basketData, basketSource]);

    // Use the appropriate basket and setter based on source
    const basket = basketSource === 'external' ? localBasket : contextBasket;

    // For external baskets, use local state that doesn't write to localStorage
    // For context baskets, use the context setter that writes to localStorage
    const setBasket = React.useCallback(
        (newBasket) => {
            if (basketSource === 'external') {
                setLocalBasket(newBasket);
            } else {
                setContextBasket(newBasket);
            }
        },
        [basketSource, setLocalBasket, setContextBasket]
    );

    const [isBulkEdit, setIsBulkEdit] = React.useState(false);
    const amountRef = useRef(null);
    const discountRef = useRef(null);
    const taxRef = useRef(null);
    const queryClient = useQueryClient();

    const apiRef = useGridApiRef();

    // Column visibility state
    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
        StokID: false,
        aaroData: false,
        // StokKodu is now visible by default to help users identify missing codes
    });

    // Basket name state - use external name if available
    const [basketName, setBasketName] = React.useState(basketData?.name || title || 'Sepet');
    const [isEditingName, setIsEditingName] = React.useState(false);
    const [tempBasketName, setTempBasketName] = React.useState(basketData?.name || title || 'Sepet');

    // Update states
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [updatingRowId, setUpdatingRowId] = React.useState(null);
    const [rowValidityStatus, setRowValidityStatus] = React.useState({});
    const [isSaving, setIsSaving] = React.useState(false);

    // Delete dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    // Update basket name when external data changes
    React.useEffect(() => {
        if (basketData?.name) {
            setBasketName(basketData.name);
            setTempBasketName(basketData.name);
        }
    }, [basketData?.name]);

    const severityDecider = (discount, tax) => {
        const profit = calculateProfit(discount, tax);
        if (profit >= 81) return ['high', 'İyi', 'success'];
        if (profit < 81 && profit >= 60) return ['medium', 'Normal', 'info'];
        if (profit < 60 && profit >= 40) return ['low', 'Düşük', 'warning'];
        if (profit < 40) return ['critical', 'Dip', 'error'];
    };

    const columns = [
        // Only show edit/delete column if not read-only
        ...(isReadOnly
            ? []
            : [
                  {
                      field: 'edit',
                      headerName: 'Sil',
                      flex: 0.3,
                      renderCell: (params) => (
                          <IconButton
                              aria-label="delete"
                              size="small"
                              onClick={() => {
                                  const currentBasket = JSON.parse(JSON.stringify(basket));
                                  for (let i = 0; i < currentBasket.length; i++) {
                                      if (currentBasket[i].StokID === params.id) {
                                          currentBasket.splice(i, 1);
                                      }
                                  }
                                  setBasket(currentBasket);
                              }}
                          >
                              <DeleteIcon fontSize="small" />
                          </IconButton>
                      ),
                      editable: false,
                  },
              ]),
        {
            field: 'profit',
            headerName: 'Kar',
            flex: 1,
            renderCell: (params) => {
                const [profitLevel, profitText] = severityDecider(params.row.Discount, params.row.Tax);
                return <ProfitChip label={profitText} profitLevel={profitLevel} size="small" />;
            },
            editable: false,
        },
        { field: 'StokID', headerName: 'StokID', flex: 0.5, hideable: true },
        {
            field: 'StokKodu',
            headerName: 'Stok Kodu',
            flex: 1.5,
            hideable: true,
            renderCell: (params) => {
                const value = params.value;

                // Check if any required field is missing
                const hasMissingFields =
                    !params.row.StokID ||
                    !params.row.StokKodu ||
                    params.row.StokKodu.trim() === '' ||
                    !params.row.StokAdi ||
                    params.row.StokAdi.trim() === '' ||
                    !params.row.Price ||
                    params.row.Price <= 0 ||
                    !params.row.Amount ||
                    params.row.Amount <= 0;

                if (hasMissingFields) {
                    return (
                        <Chip
                            label="Eksik Veri"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    );
                }

                return value;
            },
        },
        { field: 'StokAdi', headerName: 'Stok Adı', flex: 2.5 },

        // Single hidden column for all Aaro data
        {
            field: 'aaroData',
            headerName: 'Aaro Verileri',
            flex: 1,
            hideable: true,
            renderCell: (params) => {
                // Show a summary or count of available data
                const jsonString = params.value;
                if (!jsonString) return 'Veri yok';
                try {
                    const data = JSON.parse(jsonString);
                    return `${Object.keys(data).length} alan`;
                } catch (error) {
                    return 'Geçersiz veri';
                }
            },
        },

        {
            field: 'Price',
            headerName: 'Birim Fiyat',
            flex: 1,
            type: 'number',
            renderCell: (params) => formatPrice(calculateDiscount(params.row.Price, 1, params.row.Discount)),
            editable: !isReadOnly,
        },
        {
            field: 'Amount',
            headerName: 'Miktar',
            flex: 1,
            type: 'number',
            editable: !isReadOnly,
        },

        {
            field: 'Discount',
            headerName: 'İskonto',
            flex: 1,
            type: 'number',
            renderCell: (params) => {
                const valueFormatted = Number(params.value).toLocaleString();
                return `${valueFormatted} %`;
            },
            editable: !isReadOnly,
        },
        {
            field: 'Tax',
            headerName: 'Vergi',
            flex: 1,
            type: 'number',
            editable: !isReadOnly,
        },

        {
            field: 'Total',
            headerName: 'Toplam',
            flex: 1,
            type: 'number',
            renderCell: (params) => {
                const valueFormatted = calculateTaxPrice(
                    calculateDiscount(params.row.Price, params.row.Amount, params.row.Discount),
                    params.row.Tax
                );
                return `${formatPrice(valueFormatted)}`;
            },
        },
    ];

    const totalAmount = React.useMemo(() => {
        if (!basket || basket.length === 0) return formatPrice(0, 'TRY');

        let total = 0;
        basket.forEach((item) => {
            const itemTotal = calculateTaxPrice(calculateDiscount(item.Price, item.Amount, item.Discount), item.Tax);
            total += itemTotal;
        });

        return formatPrice(total, 'TRY');
    }, [basket]);

    const processRowUpdate = React.useCallback(
        (newRow, oldRow) => {
            if (isReadOnly) return oldRow;

            console.log('Row update:', { newRow, oldRow });

            // Create a deep copy of the current basket
            const currentBasket = [...basket];

            // Find and update the specific item
            const itemIndex = currentBasket.findIndex((item) => item.id === newRow.id);
            if (itemIndex !== -1) {
                currentBasket[itemIndex] = { ...newRow };

                console.log('Updated basket:', currentBasket);

                // Update the basket state
                setBasket(currentBasket);
            }

            return newRow;
        },
        [basket, isReadOnly, setBasket]
    );

    const handleBulkUpdate = (field, ref) => {
        if (isReadOnly) return;

        const value = Number(ref.current.value);
        const currentBasket = basket.map((item) => ({
            ...item,
            [field]: value,
        }));

        setBasket(currentBasket);
    };

    // Bulk price update function
    const handleBulkPriceUpdate = async () => {
        if (basket.length === 0) {
            notify(false, 'Sepet boş');
            return;
        }

        setIsUpdating(true);
        const updatedBasket = [...basket];
        const newValidityStatus = {};

        try {
            for (let i = 0; i < updatedBasket.length; i++) {
                const item = updatedBasket[i];
                setUpdatingRowId(item.StokID);

                try {
                    // Fetch price data
                    const response = await getStockPrice(item.StokID);
                    const priceData = response?.data?.Model ? response.data.Model[0] : null;

                    if (priceData) {
                        const isValid = todayIsBetween(new Date(priceData.TarihBas), new Date(priceData.TarihBit));

                        // Update item data
                        updatedBasket[i] = {
                            ...item,
                            Price: isValid ? priceData.Fiyat : 0,
                            StokKodu: priceData.StokKodu,
                            StokAdi: priceData.StokAdi,
                            aaroData: JSON.stringify(priceData),
                        };

                        // Track validity status for row highlighting
                        newValidityStatus[item.StokID] = isValid;
                    } else {
                        // No data found
                        updatedBasket[i] = {
                            ...item,
                            Price: 0,
                        };
                        newValidityStatus[item.StokID] = false;
                    }
                } catch (error) {
                    console.error(`Error updating item ${item.StokID}:`, error);
                    updatedBasket[i] = {
                        ...item,
                        Price: 0,
                    };
                    newValidityStatus[item.StokID] = false;
                }

                // Wait 200ms between requests
                if (i < updatedBasket.length - 1) {
                    await new Promise((resolve) => setTimeout(resolve, 200));
                }
            }

            setBasket(updatedBasket);
            setRowValidityStatus(newValidityStatus);
            notify(true, 'Sepet güncellendi');
        } catch (error) {
            console.error('Bulk update error:', error);
            notify(false, 'Güncelleme sırasında hata oluştu');
        } finally {
            setIsUpdating(false);
            setUpdatingRowId(null);
        }
    };

    // Debug function to check authentication
    const debugAuth = () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        console.log('User from localStorage:', user);
        console.log('User token:', user.token);
        console.log('API URL:', `${config.url.API_URL}/api/kahramanlar/baskets`);
        return user.token ? true : false;
    };

    // Validate basket items before saving
    const validateBasketItems = () => {
        const invalidItems = [];

        for (let i = 0; i < basket.length; i++) {
            const item = basket[i];
            const missingFields = [];

            if (!item.StokID) missingFields.push('StokID');
            if (!item.StokKodu || item.StokKodu.trim() === '') missingFields.push('StokKodu');
            if (!item.StokAdi || item.StokAdi.trim() === '') missingFields.push('StokAdi');
            if (!item.Price || item.Price <= 0) missingFields.push('Price');
            if (!item.Amount || item.Amount <= 0) missingFields.push('Amount');

            if (missingFields.length > 0) {
                invalidItems.push({
                    index: i + 1,
                    name: item.StokAdi || `ID: ${item.StokID}`,
                    missingFields: missingFields,
                });
            }
        }

        if (invalidItems.length > 0) {
            const errorMessages = invalidItems.map(
                (item) => `Ürün ${item.index} (${item.name}): Eksik alanlar - ${item.missingFields.join(', ')}`
            );

            notify(false, `Gerekli alanları eksik olan ürünler var:\n${errorMessages.join('\n')}`);
            return false;
        }

        return true;
    };

    // Save basket to database
    const handleSaveBasket = async () => {
        // Check authentication first
        if (!debugAuth()) {
            notify(false, 'Kullanıcı oturumu bulunamadı');
            return;
        }

        if (basket.length === 0) {
            notify(false, 'Sepet boş, kaydedilemez');
            return;
        }

        if (!basketName.trim()) {
            notify(false, 'Sepet adı gerekli');
            return;
        }

        // Validate basket items
        if (!validateBasketItems()) {
            return;
        }

        setIsSaving(true);

        try {
            if (basketSource === 'context') {
                // Save new basket for context baskets
                const newBasketData = {
                    name: basketName.trim(),
                    description: `${basket.length} ürün içeren sepet`,
                    items: basket.map((item) => ({
                        id: item.id,
                        Price: item.Price,
                        Amount: item.Amount,
                        Discount: item.Discount,
                        Tax: item.Tax,
                        StokID: item.StokID,
                        StokKodu: item.StokKodu,
                        StokAdi: item.StokAdi,
                        aaroData: item.aaroData || null,
                    })),
                };

                const response = await saveBasket(newBasketData);

                if (response.data.success) {
                    notify(true, `"${basketName}" sepeti başarıyla kaydedildi`);
                    if (onBasketSave) {
                        onBasketSave(response.data.result);
                    }
                } else {
                    notify(false, response.data.message || 'Sepet kaydedilemedi');
                }
            } else if (basketSource === 'external') {
                // Update existing basket for external baskets
                const basketUpdateData = {
                    name: basketName.trim(), // Include the updated name
                    description: `${basket.length} ürün içeren sepet`,
                    items: basket.map((item) => ({
                        id: item.id,
                        Price: item.Price,
                        Amount: item.Amount,
                        Discount: item.Discount,
                        Tax: item.Tax,
                        StokID: item.StokID,
                        StokKodu: item.StokKodu,
                        StokAdi: item.StokAdi,
                        aaroData: item.aaroData || null,
                    })),
                };

                const response = await updateBasket(basketData._id, basketUpdateData);

                if (response.data.success) {
                    notify(true, `"${basketName}" sepeti başarıyla güncellendi`);
                    // Refresh baskets list to show updated data
                    queryClient.invalidateQueries(['baskets']);
                } else {
                    notify(false, response.data.message || 'Sepet güncellenemedi');
                }
            }
        } catch (error) {
            console.error('Save basket error:', error);
            const errorMessage = error.response?.data?.message || 'Sepet kaydedilirken hata oluştu';
            notify(false, errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle basket copy
    const handleBasketCopy = async () => {
        if (!basketData?._id) return;

        try {
            const response = await copyBasket(basketData._id, {
                name: `${basketName} - Kopya`,
            });

            if (response.data.success) {
                notify(true, 'Sepet başarıyla kopyalandı');
                // Refresh baskets list
                queryClient.invalidateQueries(['baskets']);
            } else {
                notify(false, response.data.message || 'Sepet kopyalanamadı');
            }
        } catch (error) {
            console.error('Copy basket error:', error);
            const errorMessage = error.response?.data?.message || 'Sepet kopyalanırken hata oluştu';
            notify(false, errorMessage);
        }
    };

    // Handle load to temporary basket
    const handleLoadToTemporaryBasket = () => {
        if (!basketData?.items || basketSource !== 'external') return;

        try {
            // Create new items with unique IDs for the temporary basket
            const temporaryBasketItems = basketData.items.map((item, index) => ({
                ...item,
            }));

            // Set the items to context basket (temporary basket)
            setContextBasket(temporaryBasketItems);

            notify(true, `"${basketName}" sepeti geçici sepete aktarıldı (${temporaryBasketItems.length} ürün)`);
        } catch (error) {
            console.error('Load to temporary basket error:', error);
            notify(false, 'Sepet geçici sepete aktarılırken hata oluştu');
        }
    };

    // Handle basket delete
    const handleBasketDelete = async () => {
        if (!basketData?._id) return;

        setIsDeleting(true);
        try {
            const response = await deleteBasket(basketData._id);

            if (response.data.success) {
                notify(true, 'Sepet başarıyla silindi');
                setDeleteDialogOpen(false);
                // Refresh baskets list
                queryClient.invalidateQueries(['baskets']);
            } else {
                notify(false, response.data.message || 'Sepet silinemedi');
            }
        } catch (error) {
            console.error('Delete basket error:', error);
            const errorMessage = error.response?.data?.message || 'Sepet silinirken hata oluştu';
            notify(false, errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    // Open delete confirmation dialog
    const openDeleteDialog = () => {
        setDeleteDialogOpen(true);
    };

    // Modern toolbar component
    function CustomToolbar() {
        const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
        const exportMenuTriggerRef = React.useRef(null);

        // Count items with missing required fields
        const missingFieldsCount = basket.filter((item) => {
            return (
                !item.StokID ||
                !item.StokKodu ||
                item.StokKodu.trim() === '' ||
                !item.StokAdi ||
                item.StokAdi.trim() === '' ||
                !item.Price ||
                item.Price <= 0 ||
                !item.Amount ||
                item.Amount <= 0
            );
        }).length;

        // Custom print function for better formatting
        const handleCustomPrint = () => {
            // Get user info from localStorage
            const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
            const userName = userInfo.name || 'Kullanıcı';
            const userPhone = userInfo.contact?.phone || '';
            const userEmail = userInfo.email || userInfo.contact?.email || '';

            const printData = basket.map((item) => ({
                'Stok Adı': item.StokAdi,
                'Birim Fiyat': formatPrice(calculateDiscount(item.Price, 1, item.Discount)),
                Miktar: item.Amount,
                Toplam: formatPrice(
                    calculateTaxPrice(calculateDiscount(item.Price, item.Amount, item.Discount), item.Tax)
                ),
            }));

            const printWindow = window.open('', '_blank');
            const printTotalAmount = totalAmount;

            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Sepet Listesi - Kahramanlar Orman Ürünleri</title>
                    <style>
                        body { 
                            font-family: Arial, sans-serif; 
                            margin: 20px; 
                            font-size: 12px;
                            line-height: 1.4;
                        }
                        .header {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            margin-bottom: 30px;
                            padding-bottom: 20px;
                            border-bottom: 2px solid #8bc34a;
                        }
                        .company-info {
                            flex: 1;
                        }
                        .company-name {
                            font-size: 18px;
                            font-weight: bold;
                            color: #2e7d32;
                            margin-bottom: 5px;
                        }
                        .company-subtitle {
                            font-size: 12px;
                            color: #666;
                            margin-bottom: 10px;
                        }
                        .logo {
                            width: 80px;
                            height: 80px;
                            margin-left: 20px;
                        }
                        .user-info {
                            background-color: #f5f5f5;
                            padding: 15px;
                            border-radius: 8px;
                            margin-bottom: 20px;
                        }
                        .user-info h3 {
                            margin: 0 0 10px 0;
                            color: #333;
                            font-size: 14px;
                        }
                        .user-details {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 15px;
                        }
                        .user-detail {
                            font-size: 12px;
                        }
                        .user-detail strong {
                            color: #2e7d32;
                        }
                        h1 { 
                            text-align: center; 
                            margin: 20px 0 30px 0; 
                            color: #2e7d32;
                            font-size: 16px;
                            font-weight: bold;
                        }
                        table { 
                            width: 100%; 
                            border-collapse: collapse; 
                            margin-bottom: 20px;
                        }
                        th, td { 
                            border: 1px solid #ddd; 
                            padding: 10px; 
                            text-align: left; 
                        }
                        th { 
                            background-color: #8bc34a; 
                            color: white;
                            font-weight: bold; 
                        }
                        .total-row {
                            font-weight: bold;
                            background-color: #e8f5e8;
                        }
                        .text-right {
                            text-align: right;
                        }
                        .footer {
                            text-align: center; 
                            margin-top: 30px; 
                            font-size: 10px; 
                            color: #666;
                            border-top: 1px solid #ddd;
                            padding-top: 15px;
                        }
                        @media print {
                            body { margin: 0; }
                            @page { margin: 15mm; }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="company-info">
                            <div class="company-name">KAHRAMANLAR ORMAN ÜRÜNLERİ</div>
                            <div class="company-subtitle">Kaliteli Orman Ürünleri ve Ahşap Malzemeler</div>
                        </div>
                        <img src="@400.png" alt="Kahramanlar Orman Ürünleri Logo" class="logo" onerror="this.style.display='none'">
                    </div>

                    <div class="user-info">
                        <h3>Temsilci Bilgileri</h3>
                        <div class="user-details">
                            <div class="user-detail">
                                <strong>Ad Soyad:</strong> ${userName}
                            </div>
                            <div class="user-detail">
                                <strong>Tarih:</strong> ${new Date().toLocaleDateString('tr-TR')}
                            </div>
                            ${
                                userPhone
                                    ? `
                            <div class="user-detail">
                                <strong>Telefon:</strong> ${userPhone}
                            </div>
                            `
                                    : ''
                            }
                            <div class="user-detail">
                                <strong>Saat:</strong> ${new Date().toLocaleTimeString('tr-TR')}
                            </div>
                            ${
                                userEmail
                                    ? `
                            <div class="user-detail">
                                <strong>E-posta:</strong> ${userEmail}
                            </div>
                            `
                                    : ''
                            }
                            <div class="user-detail">
                                <strong>Kullanıcı:</strong> ${userInfo.username || ''}
                            </div>
                        </div>
                    </div>

                    <h1>SEPET LİSTESİ</h1>
                    
                    <table>
                        <thead>
                            <tr>
                                <th>Stok Adı</th>
                                <th>Birim Fiyat</th>
                                <th>Miktar</th>
                                <th>Toplam</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${printData
                                .map(
                                    (item) => `
                                <tr>
                                    <td>${item['Stok Adı']}</td>
                                    <td class="text-right">${item['Birim Fiyat']}</td>
                                    <td class="text-right">${item['Miktar']}</td>
                                    <td class="text-right">${item['Toplam']}</td>
                                </tr>
                            `
                                )
                                .join('')}
                            <tr class="total-row">
                                <td colspan="3"><strong>GENEL TOPLAM</strong></td>
                                <td class="text-right"><strong>${printTotalAmount}</strong></td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="footer">
                        <p><strong>Kahramanlar Orman Ürünleri</strong> - Kaliteli hizmet, güvenilir çözümler</p>
                        <p>Bu belge ${new Date().toLocaleDateString('tr-TR')} tarihinde ${new Date().toLocaleTimeString(
                'tr-TR'
            )} saatinde oluşturulmuştur.</p>
                    </div>
                </body>
                </html>
            `);

            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 250);

            setExportMenuOpen(false);
        };

        return (
            <Toolbar sx={{ minHeight: '56px !important' }}>
                {/* Basket Name Section */}
                <MDBox display="flex" alignItems="center" sx={{ flex: 1, mx: 0.5 }}>
                    {isEditingName && !isReadOnly ? (
                        <TextField
                            value={tempBasketName}
                            onChange={(e) => setTempBasketName(e.target.value)}
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 120 }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    setBasketName(tempBasketName);
                                    setIsEditingName(false);
                                }
                            }}
                            onBlur={() => {
                                setBasketName(tempBasketName);
                                setIsEditingName(false);
                            }}
                            autoFocus
                        />
                    ) : (
                        <Typography
                            fontWeight="medium"
                            fontSize="1rem"
                            onClick={() => {
                                if (!isReadOnly) {
                                    setTempBasketName(basketName);
                                    setIsEditingName(true);
                                }
                            }}
                            sx={{
                                cursor: isReadOnly ? 'default' : 'pointer',
                                '&:hover': isReadOnly
                                    ? {}
                                    : {
                                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                          borderRadius: '4px',
                                          padding: '4px 8px',
                                          margin: '-4px -8px',
                                      },
                            }}
                        >
                            {basketName}
                        </Typography>
                    )}
                </MDBox>

                {/* Missing Required Fields Warning */}
                {missingFieldsCount > 0 && (
                    <Tooltip
                        title={`${missingFieldsCount} ürünün gerekli alanları eksik. Lütfen tüm alanları doldurun.`}
                    >
                        <Chip
                            label={`${missingFieldsCount} Eksik`}
                            size="small"
                            color="warning"
                            icon={<Icon>warning</Icon>}
                            sx={{ mx: 1, fontSize: '0.7rem' }}
                        />
                    </Tooltip>
                )}

                {/* Update Button */}
                {!isReadOnly && (
                    <Tooltip title="Fiyatları Güncelle">
                        <ToolbarButton
                            onClick={handleBulkPriceUpdate}
                            disabled={isUpdating || basket.length === 0}
                            color="primary"
                        >
                            {isUpdating ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                        </ToolbarButton>
                    </Tooltip>
                )}

                {/* Save Button */}
                {!isReadOnly && showSaveButton && (
                    <Tooltip title={basketSource === 'context' ? 'Sepeti Kaydet' : 'Değişiklikleri Kaydet'}>
                        <ToolbarButton
                            onClick={handleSaveBasket}
                            disabled={isSaving || basket.length === 0 || !basketName.trim()}
                            color="success"
                        >
                            {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                        </ToolbarButton>
                    </Tooltip>
                )}

                {/* Copy Button - Only for external baskets */}
                {basketSource === 'external' && basketData?._id && (
                    <Tooltip title="Sepeti Kopyala">
                        <ToolbarButton onClick={handleBasketCopy} color="info">
                            <ContentCopyIcon fontSize="small" />
                        </ToolbarButton>
                    </Tooltip>
                )}

                {/* Load to Temporary Basket Button - Only for external baskets */}
                {basketSource === 'external' && basketData?._id && (
                    <Tooltip title="Geçici Sepete Aktar">
                        <ToolbarButton onClick={handleLoadToTemporaryBasket} color="warning">
                            <Icon fontSize="small">input</Icon>
                        </ToolbarButton>
                    </Tooltip>
                )}

                {/* Delete Button - Only for external baskets */}
                {basketSource === 'external' && basketData?._id && (
                    <Tooltip title="Sepeti Sil">
                        <ToolbarButton onClick={openDeleteDialog} color="error">
                            <DeleteIcon fontSize="small" />
                        </ToolbarButton>
                    </Tooltip>
                )}

                {basketSource === 'external' && basketData?._id && (
                    <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
                )}

                <>
                    <Tooltip title="Sütunlar">
                        <ColumnsPanelTrigger render={<ToolbarButton />}>
                            <ViewColumnIcon fontSize="small" />
                        </ColumnsPanelTrigger>
                    </Tooltip>

                    <Tooltip title="Filtreler">
                        <FilterPanelTrigger
                            render={(props, state) => (
                                <ToolbarButton {...props} color="default">
                                    <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                                        <FilterListIcon fontSize="small" />
                                    </Badge>
                                </ToolbarButton>
                            )}
                        />
                    </Tooltip>

                    <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />

                    <Tooltip title="Dışa Aktar">
                        <ToolbarButton
                            ref={exportMenuTriggerRef}
                            id="export-menu-trigger"
                            aria-controls="export-menu"
                            aria-haspopup="true"
                            aria-expanded={exportMenuOpen ? 'true' : undefined}
                            onClick={() => setExportMenuOpen(true)}
                        >
                            <FileDownloadIcon fontSize="small" />
                        </ToolbarButton>
                    </Tooltip>

                    <Menu
                        id="export-menu"
                        anchorEl={exportMenuTriggerRef.current}
                        open={exportMenuOpen}
                        onClose={() => setExportMenuOpen(false)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{
                            list: {
                                'aria-labelledby': 'export-menu-trigger',
                            },
                        }}
                    >
                        <MenuItem onClick={handleCustomPrint}>Yazdır</MenuItem>
                        <ExportCsv render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
                            CSV olarak İndir
                        </ExportCsv>
                    </Menu>

                    <StyledQuickFilter>
                        <QuickFilterTrigger
                            render={(triggerProps, state) => (
                                <Tooltip title="Ara" enterDelay={0}>
                                    <StyledToolbarButton
                                        {...triggerProps}
                                        ownerState={{ expanded: state.expanded }}
                                        color="default"
                                        aria-disabled={state.expanded}
                                    >
                                        <SearchIcon fontSize="small" />
                                    </StyledToolbarButton>
                                </Tooltip>
                            )}
                        />
                        <QuickFilterControl
                            render={({ ref, ...controlProps }, state) => (
                                <StyledTextField
                                    {...controlProps}
                                    ownerState={{ expanded: state.expanded }}
                                    inputRef={ref}
                                    aria-label="Ara"
                                    placeholder="Ara..."
                                    size="small"
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: state.value ? (
                                                <InputAdornment position="end">
                                                    <QuickFilterClear
                                                        edge="end"
                                                        size="small"
                                                        aria-label="Aramayı temizle"
                                                        material={{ sx: { marginRight: -0.75 } }}
                                                    >
                                                        <CancelIcon fontSize="small" />
                                                    </QuickFilterClear>
                                                </InputAdornment>
                                            ) : null,
                                            ...controlProps.slotProps?.input,
                                        },
                                        ...controlProps.slotProps,
                                    }}
                                />
                            )}
                        />
                    </StyledQuickFilter>
                </>
            </Toolbar>
        );
    }

    return (
        <MDBox mx={3} my={2} color="white" bgColor="white" variant="gradient" borderRadius="lg" shadow="lg" opacity={1}>
            {basket?.length > 0 && (
                <DataGridPro
                    autoHeight
                    rowHeight={26}
                    rows={basket}
                    apiRef={apiRef}
                    processRowUpdate={processRowUpdate}
                    columns={columns}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                    getRowClassName={(params) => {
                        const rowId = params.row.StokID;

                        // Check if item has missing required fields
                        const hasMissingFields =
                            !params.row.StokID ||
                            !params.row.StokKodu ||
                            params.row.StokKodu.trim() === '' ||
                            !params.row.StokAdi ||
                            params.row.StokAdi.trim() === '' ||
                            !params.row.Price ||
                            params.row.Price <= 0 ||
                            !params.row.Amount ||
                            params.row.Amount <= 0;

                        if (hasMissingFields) {
                            return 'missing-required-fields-row';
                        }

                        if (updatingRowId === rowId) {
                            return 'updating-row';
                        }
                        if (rowValidityStatus[rowId] === true) {
                            return 'valid-row';
                        }
                        if (rowValidityStatus[rowId] === false) {
                            return 'invalid-row';
                        }
                        return '';
                    }}
                    initialState={{
                        pinnedColumns: { right: ['Total'] },
                        columns: {
                            columnVisibilityModel: columnVisibilityModel,
                        },
                    }}
                    sx={{
                        fontSize: pxToRem(12),
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                        fontWeight: 400,
                        lineHeight: 1.25,
                        '& .missing-required-fields-row': {
                            backgroundColor: '#fff3e0',
                            borderLeft: '4px solid #ff9800',
                            '&:hover': {
                                backgroundColor: '#ffe0b2',
                            },
                        },
                        '& .valid-row': {
                            backgroundColor: '#e8f5e8',
                            '&:hover': {
                                backgroundColor: '#d4edda',
                            },
                        },
                        '& .invalid-row': {
                            backgroundColor: '#ffebee',
                            '&:hover': {
                                backgroundColor: '#ffcdd2',
                            },
                        },
                        '& .updating-row': {
                            backgroundColor: '#e3f2fd',
                            '&:hover': {
                                backgroundColor: '#bbdefb',
                            },
                        },
                    }}
                    showToolbar
                    slots={{
                        toolbar: CustomToolbar,
                        footer: () => (
                            <>
                                <MDBox
                                    display="flex"
                                    flexDirection="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    p={1}
                                >
                                    <MDBox display="flex" gap={2} alignItems="center">
                                        {!isReadOnly && <AaroButtons />}
                                        {!isReadOnly && (
                                            <MDBox pb={2}>
                                                <MDBox>
                                                    <MDButton
                                                        variant={isBulkEdit ? 'contained' : 'outlined'}
                                                        color="dark"
                                                        size="small"
                                                        iconOnly
                                                        onClick={() => setIsBulkEdit(!isBulkEdit)}
                                                    >
                                                        {isBulkEdit ? <Icon>remove</Icon> : <Icon>add</Icon>}
                                                    </MDButton>
                                                </MDBox>
                                            </MDBox>
                                        )}
                                        {isBulkEdit && !isReadOnly && (
                                            <>
                                                <MDBox display="flex" gap={1} alignItems="center">
                                                    <TextField
                                                        inputRef={amountRef}
                                                        type="number"
                                                        label="Miktar"
                                                        defaultValue={1}
                                                        size="small"
                                                        sx={{
                                                            width: '80px',
                                                            '& .MuiOutlinedInput-root': {
                                                                height: '35px',
                                                            },
                                                        }}
                                                        slotProps={{
                                                            htmlInput: {
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                min: 0,
                                                                step: '1',
                                                            },
                                                        }}
                                                    />
                                                    <MDButton
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        onClick={() => handleBulkUpdate('Amount', amountRef)}
                                                    >
                                                        Uygula
                                                    </MDButton>
                                                </MDBox>

                                                <MDBox display="flex" gap={1} alignItems="center">
                                                    <TextField
                                                        inputRef={discountRef}
                                                        type="number"
                                                        label="İskonto"
                                                        defaultValue={0}
                                                        size="small"
                                                        sx={{
                                                            width: '80px',
                                                            '& .MuiOutlinedInput-root': {
                                                                height: '35px',
                                                            },
                                                        }}
                                                        slotProps={{
                                                            htmlInput: {
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                min: 0,
                                                                max: 100,
                                                            },
                                                        }}
                                                    />
                                                    <MDButton
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        onClick={() => handleBulkUpdate('Discount', discountRef)}
                                                    >
                                                        Uygula
                                                    </MDButton>
                                                </MDBox>

                                                <MDBox display="flex" gap={1} alignItems="center">
                                                    <TextField
                                                        inputRef={taxRef}
                                                        type="number"
                                                        label="Vergi"
                                                        defaultValue={20}
                                                        size="small"
                                                        sx={{
                                                            width: '80px',
                                                            '& .MuiOutlinedInput-root': {
                                                                height: '35px',
                                                            },
                                                        }}
                                                        slotProps={{
                                                            htmlInput: {
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                min: 0,
                                                                max: 100,
                                                            },
                                                        }}
                                                    />
                                                    <MDButton
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        onClick={() => handleBulkUpdate('Tax', taxRef)}
                                                    >
                                                        Uygula
                                                    </MDButton>
                                                </MDBox>
                                            </>
                                        )}
                                    </MDBox>
                                    <MDTypography variant="h6">Toplam: {totalAmount}</MDTypography>
                                </MDBox>
                            </>
                        ),
                    }}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Sepeti Sil</DialogTitle>
                <DialogContent>
                    <MDTypography>
                        "{basketName}" sepetini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        İptal
                    </Button>
                    <Button onClick={handleBasketDelete} color="error" variant="contained" disabled={isDeleting}>
                        {isDeleting ? <CircularProgress size={20} /> : 'Sil'}
                    </Button>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}
